import { ClipboardCheck, ExternalLink } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { copyToClipboard } from "@/lib/clipboard";

import { useFileUploadUrl, useReceivablesBoxFolder } from "../../api/use-agent-feedback";

/** Centered divider — port of ui/dividers/DividerCenterMedium.vue. */
export function DividerCenterMedium({ text }: { text: string }) {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center" aria-hidden="true">
        <div className="w-full border-t border-gray-300" />
      </div>
      <div className="relative flex justify-center">
        <span className="bg-white px-3 text-base leading-6 font-semibold text-gray-900">{text}</span>
      </div>
    </div>
  );
}

/** Port of components/agent-feedback/BoxLinks.vue. */
export function BoxLinks({ claimId }: { claimId: string }) {
  const { data: boxThirdUploadFolder } = useReceivablesBoxFolder(claimId);
  const { data: fileUploadResponse } = useFileUploadUrl(claimId);

  const publicDocumentsUrl = boxThirdUploadFolder?.publicLink;
  const fileUploadUrl = fileUploadResponse?.href;

  function copyBoxData() {
    // The text should stay in sync with the HTML
    void copyToClipboard(`Documents à signer :
${publicDocumentsUrl || ""}\n
Lien pour nous transmettre les documents :
${fileUploadUrl || ""}`);
  }

  function openLink(link?: string) {
    if (link) {
      window.open(link, "_blank");
    }
  }

  return (
    <div>
      <div>
        <DividerCenterMedium text="Nous envoyer une signature" />
        {boxThirdUploadFolder?.boxFolderId ? (
          <div className="mx-auto mt-8 max-w-2xl">
            {/* Box UI Elements uploader — not available in the React app yet. */}
            <div
              data-vue-source="components/Box/BoxFileUploader.vue"
              className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center"
            >
              <p className="text-sm font-medium text-gray-700">Uploader de fichiers Box</p>
              <p className="text-xs text-gray-500">
                Utiliser le lien de transmission ci-dessous en attendant la migration de ce widget.
              </p>
            </div>
            <p className="z-50 mt-2 px-2 text-center text-base italic">
              Et tout autre document : Carte d'identité, billet, etc.
            </p>
          </div>
        ) : (
          <div className="mx-auto mt-10 flex justify-center">
            <Spinner className="h-6 w-6" />
          </div>
        )}
      </div>
      <div className="mt-16">
        <div className="mb-6">
          <DividerCenterMedium text="Liens partageables" />
        </div>
        <div className="mb-8 overflow-hidden rounded-lg border bg-gray-50 p-5 text-sm font-normal text-ellipsis shadow">
          <p>
            Document à signer :
            <a
              className="block truncate text-blue-600 underline hover:text-blue-800"
              href={publicDocumentsUrl || "#"}
              target="_blank"
              rel="noreferrer"
            >
              {publicDocumentsUrl || "Chargement..."}
            </a>
          </p>
          <p className="mt-6">
            Pour nous transmettre une signature :
            <a
              className="block truncate text-blue-600 underline hover:text-blue-800"
              href={fileUploadUrl || "#"}
              target="_blank"
              rel="noreferrer"
            >
              {fileUploadUrl || "Chargement..."}
            </a>
          </p>
          <div className="mt-4 inline-flex gap-x-6">
            <Button
              variant="outline"
              disabled={!publicDocumentsUrl}
              onClick={() => openLink(publicDocumentsUrl)}
            >
              <ExternalLink className="mr-1 h-4 w-4" />
              Voir les documents
            </Button>
            <Button variant="outline" disabled={!fileUploadUrl} onClick={copyBoxData}>
              <ClipboardCheck className="mr-1 h-4 w-4" />
              Copier ce texte
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
